/**
 * Environment-specific settings an SPA loads from the API before it boots
 * (GET {apiUrl}config/spa/{app}). Keeping these out of environment.ts lets one
 * build be deployed to every environment; each environment's API serves its own
 * B2C tenant settings from App Service configuration.
 */
export interface SpaAuthConfig {
  clientId: string;
  authority: string;
  passwordResetAuthority: string | null;
  knownAuthorities: string[];
}

export interface SpaClientConfigBase {
  auth: SpaAuthConfig;
  apiScopes: string[];
}

export class SpaClientConfigError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'SpaClientConfigError';
  }
}

/**
 * Fetches the SPA client config. Uses plain fetch (not HttpClient) because it runs
 * before bootstrap, and must not go through the MSAL interceptor, which this config
 * is needed to create.
 *
 * @param requiredKeys app-specific top-level string settings that must be present.
 */
export async function fetchSpaClientConfig<T extends SpaClientConfigBase>(
  url: string,
  requiredKeys: ReadonlyArray<Exclude<keyof T, keyof SpaClientConfigBase>> = [],
  fetchFn: typeof fetch = (input, init) => fetch(input, init)
): Promise<T> {
  let response: Response;
  try {
    response = await fetchFn(url, { headers: { Accept: 'application/json' } });
  } catch (err) {
    throw new SpaClientConfigError(`Could not reach ${url}`, { cause: err });
  }

  if (!response.ok) {
    throw new SpaClientConfigError(`${url} returned ${response.status}`);
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch (err) {
    throw new SpaClientConfigError(`${url} did not return JSON`, { cause: err });
  }

  assertSpaClientConfig<T>(body, requiredKeys as ReadonlyArray<string>);
  return body;
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function isNonEmptyStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.length > 0 && value.every(isNonEmptyString);
}

function assertSpaClientConfig<T extends SpaClientConfigBase>(
  body: unknown,
  requiredKeys: ReadonlyArray<string>
): asserts body is T {
  const config = body as Partial<Record<string, unknown>> | null;
  const auth = config?.['auth'] as Partial<Record<string, unknown>> | undefined;
  const problems: string[] = [];

  if (!isNonEmptyString(auth?.['clientId'])) problems.push('auth.clientId');
  if (!isNonEmptyString(auth?.['authority'])) problems.push('auth.authority');
  if (!isNonEmptyStringArray(auth?.['knownAuthorities'])) problems.push('auth.knownAuthorities');
  if (!isNonEmptyStringArray(config?.['apiScopes'])) problems.push('apiScopes');
  for (const key of requiredKeys) {
    if (!isNonEmptyString(config?.[key])) problems.push(key);
  }

  if (problems.length > 0) {
    throw new SpaClientConfigError(`SPA client config is missing ${problems.join(', ')}`);
  }
}

/**
 * Replaces the page with a static message when the app can't boot because its
 * config couldn't be loaded. Static text only; nothing from the failed response is rendered.
 */
export function showSpaBootFailure(doc: Document = document): void {
  const container = doc.createElement('div');
  container.setAttribute('role', 'alert');
  container.style.cssText = 'font-family: sans-serif; max-width: 32rem; margin: 4rem auto; padding: 0 16px; text-align: center;';

  const heading = doc.createElement('h1');
  heading.textContent = 'We couldn’t load OutdoorAccess';
  heading.style.fontSize = '1.5rem';

  const message = doc.createElement('p');
  message.textContent = 'The site is having trouble reaching our servers. Please refresh the page in a moment.';

  container.append(heading, message);
  doc.body.replaceChildren(container);
}
